import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

const lgoButtonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border",
        ghost: "bg-transparent text-foreground hover:bg-muted",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        link:
          "bg-transparent text-primary underline-offset-4 hover:underline px-0 h-auto",
      },
      size: {
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface LGoButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof lgoButtonVariants> {
  loading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  asChild?: boolean;
}

export const LGoButton = React.forwardRef<HTMLButtonElement, LGoButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading,
      iconLeft,
      iconRight,
      asChild = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const inner = (
      <>
        {loading ? (
          <Loader2 className="animate-spin" aria-hidden="true" />
        ) : (
          iconLeft
        )}
        {children}
        {!loading && iconRight}
      </>
    );
    // Slot expects exactly one child element. When asChild, wrap the
    // user-supplied child + icon slots in cloneElement so the Slot only
    // sees one element.
    if (asChild && React.isValidElement(children)) {
      return (
        <Comp
          ref={ref}
          className={cn(lgoButtonVariants({ variant, size }), className)}
          aria-busy={loading || undefined}
          {...props}
        >
          {React.cloneElement(
            children as React.ReactElement<{ children?: React.ReactNode }>,
            undefined,
            <>
              {loading ? (
                <Loader2 className="animate-spin" aria-hidden="true" />
              ) : (
                iconLeft
              )}
              {(children as React.ReactElement<{ children?: React.ReactNode }>).props.children}
              {!loading && iconRight}
            </>,
          )}
        </Comp>
      );
    }
    return (
      <Comp
        ref={ref}
        className={cn(lgoButtonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {inner}
      </Comp>
    );
  },
);
LGoButton.displayName = "LGoButton";

export { lgoButtonVariants };
